import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  excluirOcupacao,
  filtrar,
  listarOcupacoes,
  salvarOcupacao,
  type Ocupacao,
} from "../lib/ocupacoes";
import { CAMPO, Recado } from "./ui";
import { IconeEditar, IconeFechar, IconeLixeira } from "./icones";

/**
 * Admin → Ocupações do MEI: a lista que responde "esta atividade pode ser MEI?".
 *
 * ## Por que uma aba própria, e não dentro de Conteúdo
 *
 * Conteúdo é área → página → blocos, um texto que alguém escreve. Isto é uma
 * TABELA de referência: 502 linhas com campos fixos, onde o trabalho é corrigir
 * um CNAE ou mover uma atividade de lado quando o CGSN publica resolução. Enfiar
 * na aba de conteúdo obrigaria a inventar "uma página por ocupação", e a lista
 * pararia de ser pesquisável.
 *
 * ## Por que a lista é editável aqui
 *
 * Decisão de Victor (21/08): a lista veio do Bubble como está — cadastrada em
 * 2023 e nunca atualizada — e a manutenção passa a ser da equipe de conteúdo. A
 * alternativa era esperar por uma lista oficial nova, e a busca continuaria não
 * existindo para o contador. Com tela, corrigir uma linha custa um minuto; sem
 * tela, custa uma migration.
 *
 * ## O que esta tela NÃO decide
 *
 * Quem pode editar é `pode_editar('ocupacoes')` (000297), DENTRO de cada RPC (000086). A tela não
 * esconde botão para se proteger.
 */
export default function AdminOcupacoes() {
  const [lista, setLista] = useState<Ocupacao[] | null>(null);
  const [erro, setErro] = useState(false);
  const [termo, setTermo] = useState("");
  const [editando, setEditando] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  const recarregar = async () => {
    const r = await listarOcupacoes();
    if (r === null) setErro(true);
    else {
      setLista(r);
      setErro(false);
    }
  };

  useEffect(() => {
    void recarregar();
  }, []);

  const achadas = useMemo(() => (lista ? filtrar(lista, termo) : []), [lista, termo]);

  if (erro) {
    return (
      <p className="rounded-xl bg-superficie-2 p-6 text-sm leading-relaxed text-cinza">
        Não conseguimos carregar a lista agora. Recarregue a página; se continuar,
        é problema de conexão com o banco.
      </p>
    );
  }
  if (!lista) return <p className="text-sm text-cinza">Carregando a lista…</p>;

  const vedadas = lista.filter((o) => o.vedada).length;

  return (
    <div>
      <h2 className="font-display text-lg font-bold text-titulo">Ocupações do MEI</h2>
      <p className="mt-1 max-w-3xl text-sm leading-relaxed text-cinza">
        A lista que o contador consulta no MEI PRO para saber se uma atividade
        pode ser MEI. Ela veio do sistema antigo, onde foi cadastrada em 2023 —
        quando o CGSN publicar resolução, é aqui que se corrige.
      </p>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
        <label className="block min-w-64 flex-1">
          <span className="mb-1.5 block text-sm font-semibold text-titulo">
            Procurar por atividade ou CNAE
          </span>
          <input
            className={CAMPO}
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="açougueiro, 4789-0/99…"
          />
        </label>
        <button
          type="button"
          className="pill-cta"
          onClick={() => {
            setCriando((v) => !v);
            setEditando(null);
          }}
        >
          {criando ? "Fechar" : "+ Nova ocupação"}
        </button>
      </div>

      <p className="mt-3 text-sm text-cinza">
        {lista.length} ocupações · {lista.length - vedadas} permitidas · {vedadas} vedadas
        {termo.trim() !== "" && ` · ${achadas.length} na busca`}
      </p>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      {criando && (
        <Formulario
          aoSalvar={async (o) => {
            const r = await salvarOcupacao(o);
            if (!r.ok) return r.mensagem;
            setCriando(false);
            setRecado({ tipo: "ok", texto: "Ocupação cadastrada." });
            await recarregar();
            return null;
          }}
          aoFechar={() => setCriando(false)}
        />
      )}

      <ul className="mt-4 space-y-2">
        {achadas.map((o) => (
          // `data-atividade` é escopo NOMEADO para prova de tela, na mesma
          // linha do `id="valores-da-regiao"` da calculadora: numa lista de 502
          // itens, achar "a minha linha" pelo texto falha assim que ela entra
          // em edição — aí o nome vira `value` de input e sai do textContent.
          <li key={o.id} data-atividade={o.atividade} className="surface p-4">
            {editando === o.id ? (
              <Formulario
                ocupacao={o}
                aoSalvar={async (nova) => {
                  const r = await salvarOcupacao({ ...nova, id: o.id });
                  if (!r.ok) return r.mensagem;
                  setEditando(null);
                  setRecado({ tipo: "ok", texto: "Ocupação salva." });
                  await recarregar();
                  return null;
                }}
                aoFechar={() => setEditando(null)}
              />
            ) : (
              <Linha
                ocupacao={o}
                aoEditar={() => {
                  setEditando(o.id);
                  setCriando(false);
                }}
                aoExcluir={async () => {
                  const r = await excluirOcupacao(o.id);
                  if (!r.ok) setRecado({ tipo: "erro", texto: r.mensagem });
                  else {
                    setRecado({ tipo: "ok", texto: `"${o.atividade}" foi excluída.` });
                    await recarregar();
                  }
                }}
              />
            )}
          </li>
        ))}
      </ul>

      {achadas.length === 0 && (
        <p className="mt-4 rounded-xl bg-superficie-2 p-5 text-sm text-cinza">
          Nenhuma ocupação com esse termo.
        </p>
      )}
    </div>
  );
}

function Linha({
  ocupacao: o,
  aoEditar,
  aoExcluir,
}: {
  ocupacao: Ocupacao;
  aoEditar: () => void;
  aoExcluir: () => Promise<void>;
}) {
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-titulo">{o.atividade}</p>
        {o.descricao && <p className="mt-0.5 text-sm leading-relaxed text-cinza">{o.descricao}</p>}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-cinza">
          <span
            className={`rounded-full px-2 py-0.5 font-bold ${
              o.vedada ? "bg-erro-fundo text-erro-tinta" : "bg-aviso-fundo text-aviso-tinta"
            }`}
          >
            {o.vedada ? "vedada" : "permitida"}
          </span>
          {o.cnae && <span className="tabular-nums">CNAE {o.cnae}</span>}
          {o.icms !== null && <span>ICMS: {o.icms ? "sim" : "não"}</span>}
          {o.iss !== null && <span>ISS: {o.iss ? "sim" : "não"}</span>}
          {o.exclusao && <span>excluída por {o.exclusao}</span>}
        </div>
      </div>
      {/* Ação de linha é ÍCONE com o verbo inteiro no title/aria-label — a
          doutrina que Victor pediu em 20/08. A confirmação de excluir continua
          por extenso: é o único momento em que a frase importa mais que o
          espaço, porque ela é o que impede o clique errado. */}
      <div className="flex shrink-0 items-center gap-2">
        {confirmando ? (
          <span className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold text-erro-tinta">Excluir esta ocupação da lista?</span>
            <button
              type="button"
              className="font-semibold text-erro-tinta underline"
              onClick={async () => {
                setConfirmando(false);
                await aoExcluir();
              }}
            >
              Sim, excluir
            </button>
            <button
              type="button"
              className="font-semibold text-link"
              onClick={() => setConfirmando(false)}
            >
              Não
            </button>
          </span>
        ) : (
          <>
            <Botao onClick={aoEditar} title="Editar a ocupação" icone={<IconeEditar />} />
            <Botao
              onClick={() => setConfirmando(true)}
              title="Excluir a ocupação"
              icone={<IconeLixeira />}
            />
          </>
        )}
      </div>
    </div>
  );
}

function Formulario({
  ocupacao,
  aoSalvar,
  aoFechar,
}: {
  ocupacao?: Ocupacao;
  /** Devolve a mensagem de erro, ou nulo quando deu certo. */
  aoSalvar: (o: {
    vedada: boolean;
    atividade: string;
    cnae: string | null;
    descricao: string | null;
    icms: boolean | null;
    iss: boolean | null;
    exclusao: string | null;
  }) => Promise<string | null>;
  aoFechar: () => void;
}) {
  const [vedada, setVedada] = useState(ocupacao?.vedada ?? false);
  const [atividade, setAtividade] = useState(ocupacao?.atividade ?? "");
  const [cnae, setCnae] = useState(ocupacao?.cnae ?? "");
  const [descricao, setDescricao] = useState(ocupacao?.descricao ?? "");
  const [icms, setIcms] = useState<string>(triEstado(ocupacao?.icms));
  const [iss, setIss] = useState<string>(triEstado(ocupacao?.iss));
  const [exclusao, setExclusao] = useState(ocupacao?.exclusao ?? "");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  return (
    <form
      className="space-y-3 rounded-xl bg-superficie-2 p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        setErro(null);
        if (!atividade.trim()) return setErro("A ocupação precisa de um nome.");
        setSalvando(true);
        const msg = await aoSalvar({
          vedada,
          atividade: atividade.trim(),
          cnae: cnae.trim() || null,
          descricao: descricao.trim() || null,
          icms: deTriEstado(icms),
          iss: deTriEstado(iss),
          exclusao: exclusao.trim() || null,
        });
        setSalvando(false);
        if (msg) setErro(msg);
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-titulo">
          {ocupacao ? "Editar ocupação" : "Nova ocupação"}
        </p>
        <Botao onClick={aoFechar} title="Fechar sem salvar" icone={<IconeFechar />} />
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-semibold text-titulo">
          Nome da atividade, como a norma escreve
        </span>
        <input className={CAMPO} value={atividade} onChange={(e) => setAtividade(e.target.value)} />
      </label>

      <label className="block">
        <span className="mb-1 block text-sm font-semibold text-titulo">
          Descrição da subclasse CNAE <span className="font-normal text-cinza">(opcional)</span>
        </span>
        <input className={CAMPO} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        {/* Desde a 000189 esta descrição NÃO é mais da ocupação: ela é do
            CNAE, no catálogo único do hub. Editar aqui edita lá, e o Radar
            Simples mostra o mesmo texto. Dizer isso com letra é o que separa
            "lista única" de armadilha — antes eram duas listas, e 37
            descrições divergiam para o mesmo código. */}
        <span className="mt-1 block text-xs text-cinza">
          Vem do catálogo de CNAEs, que é um só para o hub inteiro. Corrigir aqui corrige também
          no Radar Simples e em qualquer ocupação que use o mesmo código.
        </span>
      </label>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            CNAE <span className="font-normal text-cinza">(opcional)</span>
          </span>
          <input
            className={CAMPO}
            value={cnae}
            onChange={(e) => setCnae(e.target.value)}
            placeholder="4789-0/99"
          />
        </label>
        <TriSelect rotulo="Paga ICMS" valor={icms} aoMudar={setIcms} />
        <TriSelect rotulo="Paga ISS" valor={iss} aoMudar={setIss} />
      </div>

      <label className="flex items-start gap-2.5">
        <input
          type="checkbox"
          className="mt-0.5 size-4 accent-laranja"
          checked={vedada}
          onChange={(e) => setVedada(e.target.checked)}
        />
        <span className="text-sm leading-relaxed text-tinta">
          Esta atividade NÃO pode ser MEI
          <span className="block text-xs text-cinza">
            marque quando a atividade foi excluída da lista pelo CGSN
          </span>
        </span>
      </label>

      {/* O campo da norma só aparece do lado vedado — a RPC recusa norma em
          atividade permitida, e mostrar o campo onde ele não vale colheria a
          recusa por um dado que a tela pediu. */}
      {vedada && (
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            Norma que excluiu a atividade
          </span>
          <input
            className={CAMPO}
            value={exclusao}
            onChange={(e) => setExclusao(e.target.value)}
            placeholder="Resolução CGSN, 143/2018"
          />
        </label>
      )}

      {erro && <p className="text-sm font-semibold text-erro-tinta">{erro}</p>}
      <button type="submit" className="pill-cta" disabled={salvando}>
        {salvando ? "Salvando…" : "Salvar ocupação"}
      </button>
    </form>
  );
}

/**
 * ICMS e ISS têm TRÊS estados, e o terceiro é informação: "a lista não diz" é
 * diferente de "não paga". Um checkbox só tem dois, e usá-lo transformaria em
 * "não paga" toda linha em que o legado não informou — resposta errada dada com
 * confiança, que é pior do que a ausência.
 */
function TriSelect({
  rotulo,
  valor,
  aoMudar,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-titulo">{rotulo}</span>
      <select className={CAMPO} value={valor} onChange={(e) => aoMudar(e.target.value)}>
        <option value="">não informado</option>
        <option value="sim">sim</option>
        <option value="nao">não</option>
      </select>
    </label>
  );
}

const triEstado = (v: boolean | null | undefined) => (v === true ? "sim" : v === false ? "nao" : "");
const deTriEstado = (v: string) => (v === "sim" ? true : v === "nao" ? false : null);

function Botao({ onClick, title, icone }: { onClick: () => void; title: string; icone: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="grid size-9 shrink-0 place-items-center rounded-full border-[1.5px] border-borda text-tinta transition hover:border-laranja hover:text-laranja-texto"
    >
      {icone}
    </button>
  );
}
